/**
 * Download a run's working paper (PDF or Excel): the model of what the Test Results tabs show (model.ts), laid out
 * by the AI service. Both Download menus (Test Runs list and the results page) use this.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import axiosInstance from '../../../api/axios';
import { BuildOptions, createWorkingPaper, ScreenData, WorkingPaperModel } from './model.ts';

export type WorkingPaperFormat = 'pdf' | 'excel';

const MIME: Record<WorkingPaperFormat, string> = {
  pdf: 'application/pdf',
  excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};
const EXT: Record<WorkingPaperFormat, string> = { pdf: 'pdf', excel: 'xlsx' };

/** "CTL_000012_RUN-13_WorkingPaper.pdf" (the same name the service gives, without its date). */
export function workingPaperFileName(model: WorkingPaperModel, format: WorkingPaperFormat): string {
  const ref = (model.header.wpRef || 'WP').replace(/-OE$/, '');
  return `${[ref, model.header.runLabel, 'WorkingPaper'].filter(Boolean).join('_')}.${EXT[format]}`.replace(/[^A-Za-z0-9_.-]/g, '_');
}

/** The message of a failed blob request (the body is a Blob holding the JSON error). */
export async function blobErrorMessage(err: any): Promise<string> {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await data.text());
      return body.detail || body.error || body.message || err.message;
    } catch { /* not JSON */ }
  }
  return data?.error || data?.detail || err?.message || 'unknown error';
}

export async function downloadWorkingPaper(runId: string, format: WorkingPaperFormat, opts: BuildOptions = {},
  screen: ScreenData = {}): Promise<void> {
  const model = await createWorkingPaper(runId, opts, screen);
  const res = await axiosInstance.post(`/api/control-test-runs/export-wp-v2-${format}`, { model }, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data], { type: MIME[format] }));
  const a = document.createElement('a');
  a.href = url;
  a.download = workingPaperFileName(model, format);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
